import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { cngs as seedCngs } from '../data/cngs';
import { irregularities as seedCases } from '../data/irregularities';
import type { CaseStatus, Cng, IrregularityCase } from '../types/domain';
export interface ReplacementEntry {
  date: string;
  oldQr: string;
  newQr: string;
  reason: string;
  by: string;
}
interface PrototypeState {
  cngList: Cng[];
  visibleCngs: Cng[];
  getCng: (id: string) => Cng | undefined;
  setStage: (id: string, stage: number) => void;
  assignDriver: (cngId: string, driverId: string) => void;
  removeDriver: (cngId: string, driverId: string) => void;
  cases: IrregularityCase[];
  setCaseStatus: (id: string, status: CaseStatus, action?: string) => void;
  addCase: (c: IrregularityCase) => void;
  imported: boolean;
  setImported: (v: boolean) => void;
  driverInactive: Record<string, boolean>;
  toggleDriverActive: (id: string) => void;
  replacements: Record<string, ReplacementEntry[]>;
  addReplacement: (cngId: string, entry: ReplacementEntry) => void;
}
const PrototypeContext = createContext<PrototypeState | null>(null);
const SECOND_BATCH = 'BRTA-IMP-2026-002';
export function PrototypeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [stages, setStages] = useState<Record<string, number>>({});
  const [assignments, setAssignments] = useState<Record<string, string[]>>({});
  const [cases, setCases] = useState<IrregularityCase[]>(seedCases);
  const [imported, setImported] = useState(false);
  const [driverInactive, setDriverInactive] = useState<Record<string, boolean>>({});
  const [replacements, setReplacements] = useState<Record<string, ReplacementEntry[]>>({
    c11: [{
      date: '১১ মার্চ ২০২৬',
      oldQr: 'CNG-000011',
      newQr: 'CNG-000011-R1',
      reason: 'QR পড়া যাচ্ছে না',
      by: 'মোঃ সাজ্জাদ হোসেন'
    }],
    c1: [{
      date: '২০ ফেব্রুয়ারি ২০২৬',
      oldQr: 'CNG-000001-T',
      newQr: 'CNG-000001',
      reason: 'গাড়ির কাচ পরিবর্তন',
      by: 'মোঃ সাজ্জাদ হোসেন'
    }]
  });
  const cngList = useMemo(() => seedCngs.map(c => ({
    ...c,
    stage: stages[c.id] ?? c.stage,
    driverIds: assignments[c.id] ?? c.driverIds,
    brtaStatus: (stages[c.id] ?? c.stage) >= 2 && c.brtaStatus === 'অমিল' ? 'যাচাইকৃত' as const : c.brtaStatus
  })), [stages, assignments]);
  const visibleCngs = useMemo(() => cngList.filter(c => imported || c.importBatch !== SECOND_BATCH), [cngList, imported]);
  const getCng = useCallback((id: string) => cngList.find(c => c.id === id), [cngList]);
  const setStage = useCallback((id: string, stage: number) => {
    setStages(s => ({
      ...s,
      [id]: stage
    }));
  }, []);
  const assignDriver = useCallback((cngId: string, driverId: string) => {
    setAssignments(a => {
      const current = a[cngId] ?? seedCngs.find(c => c.id === cngId)?.driverIds ?? [];
      if (current.includes(driverId)) return a;
      return {
        ...a,
        [cngId]: [...current, driverId]
      };
    });
  }, []);
  const removeDriver = useCallback((cngId: string, driverId: string) => {
    setAssignments(a => {
      const current = a[cngId] ?? seedCngs.find(c => c.id === cngId)?.driverIds ?? [];
      return {
        ...a,
        [cngId]: current.filter(d => d !== driverId)
      };
    });
  }, []);
  const setCaseStatus = useCallback((id: string, status: CaseStatus, action?: string) => {
    setCases(cs => cs.map(c => c.id === id ? {
      ...c,
      status,
      action: action ?? c.action
    } : c));
  }, []);
  const addCase = useCallback((c: IrregularityCase) => setCases(cs => [c, ...cs]), []);
  const toggleDriverActive = useCallback((id: string) => {
    setDriverInactive(d => ({
      ...d,
      [id]: !d[id]
    }));
  }, []);
  const addReplacement = useCallback((cngId: string, entry: ReplacementEntry) => {
    setReplacements(r => ({
      ...r,
      [cngId]: [entry, ...(r[cngId] ?? [])]
    }));
  }, []);
  const value: PrototypeState = {
    cngList,
    visibleCngs,
    getCng,
    setStage,
    assignDriver,
    removeDriver,
    cases,
    setCaseStatus,
    addCase,
    imported,
    setImported,
    driverInactive,
    toggleDriverActive,
    replacements,
    addReplacement
  };
  return <PrototypeContext.Provider value={value} data-id="element-700" data-mp="193w5qhjq6k4l">{children}</PrototypeContext.Provider>;
}
export function usePrototype(): PrototypeState {
  const ctx = useContext(PrototypeContext);
  if (!ctx) throw new Error('usePrototype must be used inside PrototypeProvider');
  return ctx;
}